import { DialogContentText, Typography } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import { Box, InputAdornment } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';
import { useQueryClient } from '@tanstack/react-query';
import { Decimal } from 'decimal.js';
import { useState, useEffect } from 'react';
import strings from '../../../localization';
import StakingService, { StakingMode } from '../../../services/stakingService';
import { useConnection } from '../../../utils/connection';
import { useSendTransaction } from '../../../utils/notifications';
import { useBalanceInfo, useWallet } from '../../../utils/wallet';
import StakingSettingsDialog from '../StakingSettingsDialog';
import { StakingPlan } from './ActivateStake';

const MINIMUM_DOMI_STAKE_AMOUNT = 10;
let MAX_DOMI_STAKE_AMOUNT;
const LOCKUP_PERIOD_SECONDS = 30 * 24 * 60 * 60;

function getMaxDomiStakeAmount(plan?: string): number {
  const limits: Record<string, number> = {
    M1: 3000,
    M2: 5000,
    M3: 8000,
  };

  return limits[plan ?? ''] || 3000; 
}

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  subSection: {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  highlight: {
    color: theme.palette.primary.main,
  }
}));

interface NewDelegateStakeProps {
  stakingPlan: StakingPlan;
  stakingBackendUrl: string;
  stakingMode: StakingMode;
  isInitializedNotDelegated?: boolean;
  hasAINode?: boolean;
  plan?: string;
}

export function NewDelegateStake({ 
  stakingPlan,
  stakingBackendUrl,
  stakingMode,
  isInitializedNotDelegated = false,
  hasAINode = false,
  plan
}: NewDelegateStakeProps) {
  const classes = useStyles();
  const wallet = useWallet();
  const connection = useConnection();
  const balanceInfo = useBalanceInfo(wallet.publicKey);
  const queryClient = useQueryClient();
  hasAINode = true;

  const [sendTransaction, sending] = useSendTransaction();

  const [tokenAmount, setTokenAmount] = useState<string>('');
  const [isAllowedDelegator, setIsAllowedDelegator] = useState<boolean>(false);

  
  useEffect(() => {
    const checkAllowedStatus = async () => {
      const isAllowed = await stakingService.checkAllowedDelegator();
      setIsAllowedDelegator(isAllowed);
    };
    checkAllowedStatus();
  }, []);

  MAX_DOMI_STAKE_AMOUNT = isAllowedDelegator ? Infinity : getMaxDomiStakeAmount(plan);
 
  const stakingService = new StakingService(wallet, connection,
    stakingBackendUrl, stakingMode);

  if (!stakingPlan.validatorVotePubkey || !stakingPlan.lockupPeriodSeconds) {
    return (<>Failed to fetch staking plan info!</>);
  }
  const votePubkey = new PublicKey(stakingPlan.validatorVotePubkey!);
  const stakingLimit = stakingPlan.stakingLimit;

  const delegateStake = async () => {
    const stakeAccount = await stakingService.getStakeAccount();

    return await stakingService.delegateStake(stakeAccount, tokenAmount, stakingLimit,
      votePubkey, LOCKUP_PERIOD_SECONDS);
  };

  const handleOnPress = async () => {
    const stakeAccount = await stakingService.getStakeAccount();
  
    if (isInitializedNotDelegated) {
      try {
          const { stakeAmount } = await stakingService.registerStake(stakeAccount);
          localStorage.setItem(
            `initialStake_${stakeAccount.toBase58()}`,
            stakeAmount.toString(),
          );
      } finally {
        // Only delegate if account is already initialized
        sendTransaction(stakingService._delegateStake(stakeAccount, votePubkey), {
          onSuccess: async () => {
            queryClient.refetchQueries({
              queryKey: ['stakeAccountInfo', stakeAccount.toBase58()],
            });
          },
        });
      }
    } else {
      try {
          const { stakeAmount } = await stakingService.registerStake(stakeAccount);

          localStorage.setItem(
            `initialStake_${stakeAccount.toBase58()}`,
            stakeAmount.toString(),
          );
      } finally {
        // Normal flow for new stake accounts
        sendTransaction(delegateStake(), {
          onSuccess: async () => {
            queryClient.refetchQueries({
              queryKey: ['stakeAccountInfo', stakeAccount.toBase58()],
            });
          },
        });
    }}
  };


  const parsedInputAmount = /^\d+\.?\d*$/.test(tokenAmount)
    ? new Decimal(tokenAmount)
    : new Decimal(0);

  const validAmount =
    parsedInputAmount.greaterThanOrEqualTo(MINIMUM_DOMI_STAKE_AMOUNT) &&
    (isAllowedDelegator || parsedInputAmount.lessThanOrEqualTo(MAX_DOMI_STAKE_AMOUNT)) &&
    parsedInputAmount.lessThanOrEqualTo(balanceInfo?.amount / LAMPORTS_PER_SOL);

  const isDisabled = isInitializedNotDelegated ? sending : (!validAmount || sending);

  return (
    <>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Box flexGrow={1}>{strings.startStaking}</Box>
          <Box>
            <StakingSettingsDialog />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <DialogContentText component="div">
          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.title}>
              {strings.newStakingDelegateDescription1}
            </Typography>
          </div>
          <div className={classes.section}>
            <Typography variant="body2">
              {strings.newStakingDelegateDescription8}
            </Typography>
          </div>
        </DialogContentText>
        <TextField
          label={strings.amount}
          fullWidth
          autoFocus
          variant="outlined"
          margin="normal"
          type="number"
          disabled={!isInitializedNotDelegated && !hasAINode} 
          InputProps={{
            endAdornment: !isInitializedNotDelegated ? (  
              <InputAdornment position="end">
                <Button disabled={!hasAINode}
                  onClick={() => {
                    const userBalance = balanceInfo?.amount ?? 0;
                    const decimals = balanceInfo?.decimals ?? 0;
      
                    const availableBalance = Math.max(0, userBalance - LAMPORTS_PER_SOL);
                    const balanceInDomi = availableBalance / Math.pow(10, decimals);
                    const clampedAmount = Math.min(MAX_DOMI_STAKE_AMOUNT, balanceInDomi);
                    
                    setTokenAmount(clampedAmount.toFixed(decimals));
                  }}
                >
                  {strings.max}
                </Button>
                <Typography color="primary">
                  {balanceInfo?.tokenSymbol}
                </Typography>
              </InputAdornment>
            ) : null,
            inputProps: {
              min: MINIMUM_DOMI_STAKE_AMOUNT,
              max: MAX_DOMI_STAKE_AMOUNT,
              step: "any"
            }
          }}
          value={tokenAmount}
          onChange={(e) => {
            const value = e.target.value.trim();
            if (value === '') {
              setTokenAmount('');
            } else {
              const numValue = parseFloat(value);
              if (!isNaN(numValue)) {
                if (numValue > MAX_DOMI_STAKE_AMOUNT) {
                  setTokenAmount(MAX_DOMI_STAKE_AMOUNT.toString());
                } else {
                  setTokenAmount(value);
                }
              }
            }
          }}
          helperText={
            !isInitializedNotDelegated ? ( 
              <span
                onClick={() =>
                  setTokenAmount(
                    balanceAmountToUserAmount(
                      balanceInfo?.amount,
                      balanceInfo?.decimals,
                    ),
                  )
                }
              >
                {strings.max}:{' '}
                {balanceAmountToUserAmount(
                  balanceInfo?.amount,
                  balanceInfo?.decimals,
                )}
              </span>
            ) : null
          }
        />
        <DialogContentText>
          {strings.formatString(strings.newStakingMinDomiAmount, MINIMUM_DOMI_STAKE_AMOUNT)}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
      <Button 
      color="primary" 
      onClick={handleOnPress} 
      disabled={
        isInitializedNotDelegated 
          ? sending 
          : (!validAmount || sending) && !hasAINode
        }
    >
      {isInitializedNotDelegated ? strings.delegate : strings.stake}
    </Button>
      </DialogActions>
    </>
  );
}

function balanceAmountToUserAmount(balanceAmount: number, decimals: number) {
  return (balanceAmount / Math.pow(10, decimals)).toFixed(decimals);
}

